import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal, MaskedLines } from "@/components/site/Reveal";
import { BuyButton } from "@/components/site/BuyButton";
import { LeadCapture } from "@/components/site/LeadCapture";
import { GuaranteeLine } from "@/components/site/Guarantee";
import { useShop } from "@/lib/shop";

const launchEnd = () => {
  const raw = process.env.REACT_APP_LAUNCH_END_DATE;
  const d = raw ? new Date(raw) : null;
  return d && !Number.isNaN(d.getTime()) && d > new Date() ? d : null;
};

export const FinalCta = () => {
  const end = launchEnd();
  const { priceOf } = useShop();
  return (
    <section data-testid="final-cta-section" className="relative overflow-hidden border-t border-line">
      <div className="spotlight pointer-events-none absolute inset-0" />
      <div className="relative mx-auto max-w-7xl px-6 py-28 md:px-12 md:py-44">
        <p className="eyebrow" data-testid="urgency-note">
          {end ? `Launch price ends ${end.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}` : "Every week you wait, someone else spots the shift first"}
        </p>
        <h2 className="font-display mt-8 max-w-5xl text-5xl font-medium leading-[1] tracking-[-0.03em] text-white md:text-7xl lg:text-8xl">
          <MaskedLines inView lines={["The next shift is", "already happening.", <span key="l" className="font-serif-i text-zinc-400">Be early this time.</span>]} />
        </h2>
        <Reveal delay={0.2} className="mt-14 grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <BuyButton role="bundle" testId="final-cta-buy-button" className="text-base">Get the Bundle, {priceOf("bundle")}</BuyButton>
            <div className="mt-6"><GuaranteeLine /></div>
          </div>
          <div className="lg:col-span-6">
            <p className="mb-4 text-sm text-zinc-300">Not ready today? Get 10% off by email and decide later.</p>
            <LeadCapture source="final-cta" />
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export const MobileBuyBar = ({ role = "bundle" }) => {
  const { products, priceOf } = useShop();
  const p = products[role];
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 700);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <AnimatePresence>
      {show && (
        <motion.div initial={{ y: 100 }} animate={{ y: 0 }} exit={{ y: 100 }} transition={{ duration: 0.4 }}
          data-testid="mobile-buy-bar" className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-between gap-4 border-t border-white/10 bg-[#050505]/90 px-5 py-3 backdrop-blur-xl md:hidden">
          <div>
            <p className="font-display text-sm text-white">{role === "bundle" ? "The Bundle" : `The ${p.kind}`}</p>
            <p className="text-xs text-zinc-400">{role === "bundle" ? `${priceOf(role)} or 2 × ${priceOf(role, true)}` : `${priceOf(role)} · Instant download`}</p>
          </div>
          <BuyButton role={role} testId="mobile-buy-bar-button" className="!px-4 !py-3 text-sm">Get it</BuyButton>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
